import React, { useState, useEffect, KeyboardEvent, createRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import DragDrop from "../../../components/drag-drop/DragDrop";
import { MuiLoadingButton } from "../../../components";
import { Sponsor, SEO } from "../../../../core/models/db"; // Ajusta la ruta
import { FaRegCopy } from "react-icons/fa";
import { AddCircleOutline, Cancel, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import useDebouncedCallback from "../../../../core/hooks/@debounced-callback/useDebouncedCallback";
import SortableImageList from "../../../components/sorteable-image-list/SorteableImageList";
import { Util } from "../../../../core/util/util";



interface SponsorFormProps {
  loading?: boolean;
  initialSponsor: Sponsor; // Sponsor con o sin seo (y con sponsorImages si existen)
  checkSponsorSlug: (id: string, slug: string) => Promise<boolean>;
  onSubmit: (
    sponsor: Sponsor & { seo?: SEO },
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    },
    imgAction: {
      imageAddList: any[]
      imageUpdateList: any[]
      deleteImageList: any[]
    }
  ) => void;
}

const SponsorForm = ({
  loading,
  initialSponsor,
  onSubmit,
  checkSponsorSlug,
}: SponsorFormProps) => {
  const { t } = useTranslation();

  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugChecked, setSlugChecked] = useState<null | boolean>(null);

  const [codeChecked, setCodeChecked] = useState<null | boolean>(null);

  // Manejo de imagen principal
  const [image, setImage] = useState(initialSponsor?.image || "");
  const [oldImage, setOldImage] = useState(initialSponsor?.image || "");
  const [isEditing, setIsEditing] = useState(!initialSponsor?.id);
  const [imageName, setImageName] = useState("");

  // Lista de imágenes secundarias (galería) para el segundo DragDrop
  const [imageList, setImageList] = useState(
    // Opcionalmente, puedes inicializarlo con las imágenes existentes desde DB
    // Pero aquí se maneja en SortableImageList a través de imagesFromDB
    []
  );

  // Este ref es para el segundo DragDrop (galería)
  const refDragDropMultiple = createRef<any>();

  /**
   * Validación de formulario
   */
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required(t("screens.sponsor.form.validation.title.required") as string)
      .max(100, t("screens.sponsor.form.validation.title.max100Chars") as string),
    subtitle: Yup.string().max(
      100,
      t("screens.sponsor.form.validation.subtitle.max100Chars") as string
    ),
    code: Yup.string()
      .max(30, t("screens.sponsor.form.validation.code.max30Chars") as string),
    // .required(t("screens.sponsor.form.validation.code.required") as string),
    externalLink: Yup.string()
      .transform((value) => {
        if (!value) return value;
        const trimmedValue = value.trim();
        if (
          !trimmedValue.startsWith("http://") &&
          !trimmedValue.startsWith("https://")
        ) {
          return `https://${trimmedValue}`;
        }
        return trimmedValue;
      })
      .url(t("screens.sponsor.form.validation.externalLink.invalid") as string),
    description: Yup.string().max(
      500,
      t("screens.sponsor.form.validation.description.max500Chars") as string
    ),
    // Validación SEO
    seo: Yup.object().shape({
      slug: Yup.string()
        .max(50, "Máximo 50 caracteres")
        .matches(/^\/[a-z0-9]+\/[a-z0-9]+(?:-[a-z0-9]+)*$/, "Formato de slug inválido.")
        .required("El slug es obligatorio.")
        .test(
          "slug-availability",
          "Este slug ya está en uso.", // Mensaje de error personalizado
          async (value) => {
            if (slugError) return false;
            return true;
          }
        ),
      title: Yup.string().max(100, "Máximo 100 caracteres en SEO title."),
      description: Yup.string().max(300, "Máximo 300 caracteres en SEO description."),
    }),
    // Si quisieras validar sponsorImages, podrías hacerlo aquí
    // sponsorImages: Yup.array().of(
    //   Yup.object().shape({
    //     id: Yup.number().optional(),
    //     url: Yup.string().optional(),
    //     name: Yup.string().optional(),
    //   })
    // ),
  });

  // Debounce para validación del slug
  // const handleSlugValidation = useDebouncedCallback(async (value: string) => {
  //   if (!value) return;
  //   const isSlugUsed = await checkProductSlug(
  //     initialSponsor?.seo?.id || "",
  //     value
  //   );
  //   if (isSlugUsed) {
  //     formik.setFieldError(
  //       "seo.slug",
  //       t("screens.sponsor.form.validation.seo.slug.inUse") as string
  //     );
  //   }
  // }, 300);



  // Manejo de keywords SEO
  const [keywordChips, setKeywordChips] = useState<string[]>(
    initialSponsor?.seo?.keywords
      ? initialSponsor.seo.keywords
        .split(",")
        .map((kw) => kw.trim())
        .filter(Boolean)
      : []
  );

  useEffect(() => {
    if (initialSponsor?.seo?.keywords) {
      const chipsArr = initialSponsor.seo.keywords
        .split(",")
        .map((kw) => kw.trim())
        .filter(Boolean);
      setKeywordChips(chipsArr);
    } else {
      setKeywordChips([]);
    }
  }, [initialSponsor]);

  // Formik
  const formik = useFormik({
    initialValues: {
      id: initialSponsor?.id,
      image: initialSponsor?.image || "",
      title: initialSponsor?.title || "",
      subtitle: initialSponsor?.subtitle || "",
      code: initialSponsor?.code || "",
      externalLink: initialSponsor?.externalLink || "",
      description: initialSponsor?.description || "",
      position: initialSponsor?.position || "",
      seo: {
        id: initialSponsor?.seo?.id,
        slug: initialSponsor?.seo?.slug || "",
        title: initialSponsor?.seo?.title || "",
        description: initialSponsor?.seo?.description || "",
        keywords: initialSponsor?.seo?.keywords || "",
      },
      // Patalla: sponsorImages no es obligatorio, si quieres puedes inicializarlo vacío
      // o con las que vengan de initialSponsor
      // sponsorImages: initialSponsor?.sponsorImages || [],
    },
    validationSchema,
    onSubmit: (values) => {
      // 1. Convertir keywordChips a string
      const finalKeywords = keywordChips.join(",");

      const oldImages = [...initialSponsor.sponsorImages || []];

      // Generar las listas de imágenes para agregar, actualizar y eliminar
      const imgAction = {
        imageAddList: imageList.filter((image: any) => image.id === null), // Nuevas imágenes con id: null
        imageUpdateList: imageList.filter((image: any) => image.id !== null), // Imágenes existentes a actualizar
        deleteImageList: oldImages
          .filter(oldImage => !imageList.some(newImage => newImage.id === oldImage.id)) // Imágenes que ya no están en la nueva lista
          .map(image => image.id), // Solo los ids de las imágenes a eliminar
      };

      console.log('Acciones de imágenes:', imgAction);

      // 2. Construir sponsor con seo anidado
      const sponsorToSave: Sponsor & { seo?: SEO } = {
        ...values,
        image,
        seo: {
          ...values.seo,
          keywords: finalKeywords,
        },
        // Aquí añadimos las imágenes secundarias
        // sponsorImages: [
        //   /* 
        //     Mezclas las que ya estaban en DB (initialSponsor.sponsorImages)
        //     con las que tengas en imageList, si así deseas,
        //     o simplemente pasas las de imageList 
        //   */
        //   ...(initialSponsor?.sponsorImages || []),
        // ],
      };

      // Si quieres ignorar las que vengan de DB y usar solo lo que subió el usuario:
      // sponsorToSave.sponsorImages = imageList;

      onSubmit(sponsorToSave, { oldImage, image, nameImage: values.title }, imgAction);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    let imgList = Util.orderImagesByPosition(initialSponsor?.sponsorImages || []);
    if (isEditing) {
      setImageList([]);
      Util.delay(100).then(() => {
        setImageList(imgList);
      });
    } else if (!isEditing) {
      if (imgList.length > 0) {
        setImageList(imgList);
      }

    }
  }, [initialSponsor, isEditing])

  /**
   * Drag & Drop de la imagen principal
   */
  const handleFiles = (files: any[]) => {
    if (files.length === 0) {
      setImage("");
      formik.setFieldValue("image", "");
    } else if (files?.[0].blob) {
      const file = files[0];
      const url = URL.createObjectURL(file.blob);
      setImage(url);
      formik.setFieldValue("image", url);
    }
  };

  /**
   * Drag & Drop de la galería (imágenes secundarias).
   * Puedes personalizar este handler para almacenar
   * la info en imageList según necesites.
   */
  const handleGalleryFiles = (files: any[]) => {
    if (!files?.length) return;
    // Por cada file, generamos un objeto con name, url, etc.
    const newImages = files.map((file, index) => {
      const url = URL.createObjectURL(file.blob);
      return {
        id: null, // si aún no existe en DB
        name: file.name,
        url,
        position: imageList.length + index + 1, // posición basada en el número de items actuales
      };
    });

    setImageList((prev) => [...prev, ...newImages]);
  };

  // Reemplaza "changeToBackup" para la imagen principal
  const changeToBackup = () => {
    setImage(null);
    setImageName("");
    setTimeout(() => {
      setImage(initialSponsor?.image);
      setImageName(
        initialSponsor?.image?.substring(
          initialSponsor?.image?.lastIndexOf("/") + 1
        )
      );
      setOldImage(initialSponsor?.image);
    }, 200);
  };

  const handleCancelClick = () => {
    formik.resetForm();
    changeToBackup();
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Manejo de keywords
  const [newKeyword, setNewKeyword] = useState("");
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  const handleRemoveKeyword = (kw: string) => {
    setKeywordChips((prev) => prev.filter((k) => k !== kw));
  };

  const handleKeywordKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleAddKeyword = () => {
    const kwTrim = newKeyword.trim();

    if (!kwTrim) {
      setKeywordsError("La palabra clave no puede estar vacía.");
      return;
    }

    if (keywordChips.includes(kwTrim)) {
      setKeywordsError("Esta palabra clave ya está en la lista.");
      return;
    }

    if (keywordChips.length >= 10) {
      setKeywordsError("No puedes añadir más de 10 palabras clave.");
      return;
    }

    const isValidKeyword = /^[a-zA-Z0-9]+$/.test(kwTrim);
    if (!isValidKeyword) {
      setKeywordsError(
        "Las palabras clave solo pueden contener caracteres alfanuméricos."
      );
      return;
    }

    // Añadir la palabra clave
    setKeywordChips((prev) => [...prev, kwTrim]);
    setNewKeyword("");
    setKeywordsError(null);
  };


  const handleSlugValidation = useDebouncedCallback(async (value: string) => {
    if (!value) return;
    const used = await checkSponsorSlug(initialSponsor?.seo?.id || "", value);

    setSlugChecked(!used);
    setSlugError(used ? "Este slug ya está en uso." : null);
    if (used) {
      formik.setFieldError("seo.slug", "Este slug ya está en uso.");
    } else {
      formik.setFieldError("seo.slug", undefined);
    }
  }, 300);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box>
        {/* Drag & Drop para imagen principal */}
        <DragDrop
          id="sponsor-image"
          valueDefault={image}
          disabled={loading || !isEditing}
          directoryNameList={["sponsor", "images"]}
          maxSizeMB={2}
          typeAllowed={["image/*"]}
          isOnlyOneFile={true}
          isBlob={true}
          specialCase={true}
          showFileList={false}
          handleFiles={handleFiles}
        />

        {/* CAMPOS DEL SPONSOR */}
        <Grid container spacing={2} mt={2}>
          {/* Campo Título */}
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label={t("screens.sponsor.form.labels.title") + "*"}
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.title && formik.errors.title)}
              helperText={
                formik.touched.title &&
                  typeof formik.errors.title === "string"
                  ? formik.errors.title
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            />
          </Grid>

          {/* Campo Subtítulo */}
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label={t("screens.sponsor.form.labels.subtitle")}
              name="subtitle"
              value={formik.values.subtitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.subtitle && formik.errors.subtitle
              )}
              helperText={
                formik.touched.subtitle &&
                  typeof formik.errors.subtitle === "string"
                  ? formik.errors.subtitle
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            />
          </Grid>

          {/* Campo Código */}
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="end">
              <TextField
                variant="standard"
                label={t("screens.sponsor.form.labels.code")}
                name="code"
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.code && formik.errors.code)}
                helperText={
                  formik.touched.code &&
                    typeof formik.errors.code === "string"
                    ? formik.errors.code
                    : undefined
                }
                disabled={loading || (!isEditing && !!initialSponsor?.id)}
                fullWidth
              />

              <Tooltip title="Copiar código">
                <Button
                  variant="outlined"
                  size="small"
                  style={{
                    minWidth: "30px",
                    height: "30px",
                    marginLeft: "10px",
                    padding: 0,
                    borderRadius: "4px",
                  }}
                  onClick={() =>
                    navigator.clipboard.writeText(formik?.values?.code)
                  }
                  disabled={!formik.values.code || loading}
                >
                  <FaRegCopy size={16} />
                </Button>
              </Tooltip>
            </Box>
          </Grid>

          {/* Campo URL externa */}
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label={t("screens.sponsor.form.labels.externalLink")}
              name="externalLink"
              value={formik.values.externalLink.replace(/^https?:\/\//, "")}
              onChange={(e) => {
                const value = e.target.value.trim();
                // Evitar que el usuario agregue http:// o https:// manualmente
                formik.setFieldValue(
                  "externalLink",
                  value.startsWith("http://") || value.startsWith("https://")
                    ? value
                    : value
                );
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (
                  !value.startsWith("http://") &&
                  !value.startsWith("https://")
                ) {
                  formik.setFieldValue("externalLink", `https://${value}`);
                }
                formik.handleBlur(e);
              }}
              error={Boolean(
                formik.touched.externalLink && formik.errors.externalLink
              )}
              helperText={
                formik.touched.externalLink &&
                  typeof formik.errors.externalLink === "string"
                  ? formik.errors.externalLink
                  : "URL externa del sponsor (sin https://, se añadirá automáticamente)"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="textSecondary">
                      https://
                    </Typography>
                  </InputAdornment>
                ),
              }}
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              variant="standard"
              label={t("screens.sponsor.form.labels.position")}
              name="position"
              value={formik.values.position}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.position && formik.errors.position)}
              helperText={
                formik.touched.position &&
                  typeof formik.errors.position === "string"
                  ? formik.errors.position
                  : "Posición del sponsor en la página."
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            >
              {/* Opciones del select */}
              <MenuItem value="0">Muy alta</MenuItem>
              <MenuItem value="1">Alta</MenuItem>
              <MenuItem value="2">Media</MenuItem>
              <MenuItem value="3">Baja</MenuItem>
              <MenuItem value="4">Muy baja</MenuItem>
            </TextField>
          </Grid>


          {/* Campo Descripción */}
          <Grid item xs={12}>
            <TextField
              variant="standard"
              label={t("screens.sponsor.form.labels.description")}
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.description && formik.errors.description
              )}
              helperText={
                formik.touched.description &&
                  typeof formik.errors.description === "string"
                  ? formik.errors.description
                  : undefined
              }
              multiline
              rows={4}
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* SECCIÓN DE GALERÍA DE IMÁGENES */}
        <div className="mt-10">
          <div className="text-2xl font-medium mb-4">Imágenes</div>
        </div>

        {/* Segundo DragDrop para subir múltiples imágenes */}
        <div>
          <DragDrop
            disabled={!isEditing}
            // styleDD={{ height: "200px" }}
            isBlob={true}
            ref={refDragDropMultiple}
            id={"drag-drop-sponsor-gallery"}
            directoryNameList={["sponsor", "images"]}
            maxSizeMB={2}
            typeAllowed={["image/gif", "image/jpeg", "image/jpg", "image/png"]}
            specialCase={true}
            showFileList={false}
            handleFiles={handleGalleryFiles}
          />

          {/* Si hay imágenes, mostramos el SortableImageList */}
          {(
            // El array sponsorImages del initialSponsor
            // + lo que venga en imageList
            (initialSponsor?.sponsorImages &&
              initialSponsor.sponsorImages.length > 0) ||
            imageList.length > 0
          ) && (
              <SortableImageList
                id={initialSponsor?.id ? String(initialSponsor.id) : "temp-id"}
                disabled={loading || (!isEditing && !!initialSponsor?.id)}
                refDragDrop={refDragDropMultiple}
                // Las imágenes que vienen de DB
                imagesFromDB={initialSponsor?.sponsorImages || []}
                // Las nuevas imágenes que el usuario suba en esta sesión
                images={imageList}
                setImages={setImageList}
              />
            )}
        </div>


        {/* <div style={{ maxWidth: '500px', overflow: 'auto' }}>
          <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
            {JSON.stringify(imageList, null, 2)}
          </pre>
        </div> */}



        {/* APARTADO SEO */}
        <Grid container spacing={2} mt={4}>
          <Grid item xs={12}>
            <div className="text-2xl font-medium mb-4">SEO</div>
          </Grid>

          {/* Campo Slug */}
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label="Slug*"
              name="seo.slug"
              value={formik.values.seo.slug.replace(/^\/afiliados\//, "")}
              onChange={(e) => {
                let inputValue = e.target.value;
                inputValue = inputValue.replace(/^afiliados\//, "");
                const prefixedValue = `/afiliados/${inputValue}`;
                formik.setFieldValue("seo.slug", prefixedValue);
                handleSlugValidation(prefixedValue);
              }}
              onBlur={(e) => {
                let inputValue = formik.values.seo.slug;
                if (!inputValue.startsWith("/afiliados/")) {
                  inputValue = `/afiliados/${inputValue}`;
                }
                formik.setFieldValue("seo.slug", inputValue);
                formik.handleBlur(e);
              }}
              error={Boolean(formik.touched.seo?.slug && formik.errors.seo?.slug)}
              helperText={
                formik.touched.seo?.slug &&
                  typeof formik.errors.seo?.slug === "string"
                  ? formik.errors.seo?.slug
                  : "Identificador único en la URL, como '/mi-titulo-seo'."
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="textSecondary">
                      /afiliados/
                    </Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isEditing && slugChecked !== null && (
                      <IconButton edge="end">
                        {slugChecked && !formik?.errors?.seo?.slug ? (
                          <CheckIcon color="success" />
                        ) : (
                          <CloseIcon color="error" />
                        )}
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Campo Título SEO */}
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label="SEO Title"
              name="seo.title"
              value={formik.values.seo.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.seo?.title && formik.errors.seo?.title)}
              helperText={
                formik.touched.seo?.title &&
                  typeof formik.errors.seo?.title === "string"
                  ? (formik.errors.seo?.title as string)
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              fullWidth
            />
          </Grid>

          {/* Campo Description SEO */}
          <Grid item xs={12}>
            <TextField
              variant="standard"
              label="SEO Description"
              name="seo.description"
              value={formik.values.seo.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.seo?.description && formik.errors.seo?.description
              )}
              helperText={
                formik.touched.seo?.description &&
                  typeof formik.errors.seo?.description === "string"
                  ? (formik.errors.seo?.description as string)
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialSponsor?.id)}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>

          {/* Campo Keywords - Chips */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Palabras clave (Keywords)
            </Typography>

            <Box display="flex" gap={1}>
              <TextField
                variant="standard"
                label="Nueva palabra clave"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                disabled={loading || (!isEditing && !!initialSponsor?.id)}
              />
              <div className="flex items-end mt-2.5">
                <IconButton
                  color="primary"
                  onClick={handleAddKeyword}
                  disabled={!newKeyword.trim() || loading || (!isEditing && !!initialSponsor?.id)}
                >
                  <AddCircleOutline />
                </IconButton>
              </div>
            </Box>

            <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
              {keywordChips.map((kw) => (
                <Chip
                  key={kw}
                  label={kw}
                  onDelete={() => handleRemoveKeyword(kw)}
                  deleteIcon={<Cancel />}
                  disabled={loading || (!isEditing && !!initialSponsor?.id)}
                />
              ))}
            </Box>
            {keywordsError && (
              <Typography variant="body2" color="error" className="mt-2">
                {keywordsError}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* BOTONES DE ACCIÓN */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={5}>
          {/* Crear */}
          {!initialSponsor?.id && (
            <div className="w-auto">
              <MuiLoadingButton
                title={t("screens.sponsor.form.buttons.create")}
                variant="contained"
                color="primary"
                type="submit"
                disabled={!formik.isValid || loading || slugChecked === false}
                isLoading={loading}
              />
            </div>
          )}

          {/* Editar / Guardar */}
          {initialSponsor?.id && !isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditClick}
              disabled={loading}
            >
              {t("screens.sponsor.form.buttons.edit")}
            </Button>
          )}
          {initialSponsor?.id && isEditing && (
            <div className="flex flex-row gap-2">
              <Button
                className="flex-1"
                variant="outlined"
                color="primary"
                onClick={handleCancelClick}
                disabled={loading}
              >
                {t("screens.sponsor.form.buttons.cancel")}
              </Button>
              <div className="w-auto">
                <MuiLoadingButton
                  title={t("screens.sponsor.form.buttons.save")}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!formik.isValid || loading || slugChecked === false}
                  isLoading={loading}
                />
              </div>
            </div>
          )}
        </Box>
      </Box>
    </form>
  );
};

export default SponsorForm;
